/**
 * ClickAnimations - Satisfying click feedback system
 * Particles, ripples, and haptic feedback for mobile gaming excellence
 */

export class ClickAnimations {
  private particleContainer: HTMLElement;
  private rippleContainer: HTMLElement;

  constructor() {
    // Create particle container
    this.particleContainer = document.createElement('div');
    this.particleContainer.className = 'particle-container';
    this.particleContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 999;
    `;
    document.body.appendChild(this.particleContainer);

    // Create ripple container
    this.rippleContainer = document.createElement('div');
    this.rippleContainer.className = 'ripple-container';
    this.rippleContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 998;
    `;
    document.body.appendChild(this.rippleContainer);
  }

  /**
   * Trigger click animation at button position
   */
  triggerClickAnimation(button: HTMLElement) {
    const rect = button.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Trigger all effects
    this.createParticleBurst(centerX, centerY);
    this.createRipple(centerX, centerY);
    this.triggerButtonAnimation(button);
    this.triggerHaptic('light');
  }

  /**
   * Trigger auto-click indicator (floating +1)
   */
  triggerAutoClickIndicator(button: HTMLElement) {
    const rect = button.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 4;

    this.createFloatingNumber('+1', x, y);
  }

  /**
   * Create particle burst effect
   */
  private createParticleBurst(x: number, y: number) {
    const particleCount = 5;
    const colors = ['#00d4ff', '#7dd3c0', '#00ff88', '#3fb68b'];

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'click-particle';
      
      // Random direction
      const angle = (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.5;
      const distance = 30 + Math.random() * 20;
      const endX = x + Math.cos(angle) * distance;
      const endY = y + Math.sin(angle) * distance;
      
      // Random color
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      particle.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        width: 6px;
        height: 6px;
        background: ${color};
        border-radius: 50%;
        box-shadow: 0 0 8px ${color};
        opacity: 1;
        pointer-events: none;
        transform: translate(-50%, -50%);
        animation: particle-burst-${i} 400ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      `;

      // Create unique keyframe animation
      const styleSheet = document.styleSheets[0];
      const keyframes = `
        @keyframes particle-burst-${i} {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(${endX - x}px, ${endY - y}px) scale(0.2);
            opacity: 0;
          }
        }
      `;
      
      try {
        if (styleSheet) {
          styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
        }
      } catch (e) {
        // Ignore if rule already exists
      }

      this.particleContainer.appendChild(particle);

      // Remove after animation
      setTimeout(() => particle.remove(), 400);
    }
  }

  /**
   * Create ripple effect
   */
  private createRipple(x: number, y: number) {
    const ripple = document.createElement('div');
    ripple.className = 'click-ripple';
    
    ripple.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      width: 0;
      height: 0;
      border: 2px solid #00d4ff;
      border-radius: 50%;
      opacity: 0.6;
      pointer-events: none;
      transform: translate(-50%, -50%);
      animation: ripple-expand 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    `;

    this.rippleContainer.appendChild(ripple);

    // Remove after animation
    setTimeout(() => ripple.remove(), 600);
  }

  /**
   * Trigger button press animation
   */
  private triggerButtonAnimation(button: HTMLElement) {
    button.classList.add('btn-pressed');
    
    setTimeout(() => {
      button.classList.remove('btn-pressed');
    }, 200);
  }

  /**
   * Create floating number indicator (+1, +10, etc.)
   */
  private createFloatingNumber(text: string, x: number, y: number) {
    const number = document.createElement('div');
    number.className = 'floating-number';
    number.textContent = text;
    
    number.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.7rem;
      font-weight: 700;
      color: #00ff88;
      text-shadow: 0 0 8px rgba(0, 255, 136, 0.8);
      opacity: 1;
      pointer-events: none;
      transform: translate(-50%, 0);
      animation: float-up 1000ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
      z-index: 1000;
    `;

    this.particleContainer.appendChild(number);

    // Remove after animation
    setTimeout(() => number.remove(), 1000);
  }

  /**
   * Trigger haptic feedback (if supported)
   */
  private triggerHaptic(intensity: 'light' | 'medium' | 'heavy') {
    // Check for vibration API support
    if (!navigator.vibrate) return;

    const patterns = {
      light: 10,
      medium: 25,
      heavy: 50,
    };

    try {
      navigator.vibrate(patterns[intensity]);
    } catch (e) {
      // Vibration not supported or denied
    }
  }

  /**
   * Trigger milestone celebration (larger effect)
   */
  triggerMilestoneCelebration(button: HTMLElement) {
    const rect = button.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Extra large particle burst
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        this.createParticleBurst(centerX, centerY);
      }, i * 100);
    }

    // Heavy haptic
    this.triggerHaptic('heavy');

    // Screen flash
    this.createScreenFlash();
  }

  /**
   * Create screen flash effect
   */
  private createScreenFlash() {
    const flash = document.createElement('div');
    flash.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 212, 255, 0.2);
      opacity: 1;
      pointer-events: none;
      z-index: 9999;
      animation: screen-flash 300ms ease-out forwards;
    `;

    document.body.appendChild(flash);

    setTimeout(() => flash.remove(), 300);
  }

  /**
   * Cleanup
   */
  destroy() {
    this.particleContainer.remove();
    this.rippleContainer.remove();
  }
}
